import { NavLink, Outlet, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart3,
  Bot,
  Calendar,
  ChevronLeft,
  FolderOpen,
  LayoutDashboard,
  MessageSquare,
  Palette,
  Settings,
  ShieldCheck,
  UsersRound,
} from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';
import { Badge } from '@/components/ui/Badge';
import { workspacesApi } from '@/features/workspaces/api';
import { usePermission } from '@/hooks/usePermission';
import { resolveFileUrl } from '@/utils/fileUrl';
import type { Workspace, WorkspaceVisibility } from '@/features/workspaces/types';

interface Tab {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  end?: boolean;
  permission?: string;
}

const VISIBILITY_TONE: Record<WorkspaceVisibility, 'gray' | 'blue' | 'green'> = {
  private: 'gray',
  team: 'blue',
  organization: 'green',
};

const tabs: Tab[] = [
  { to: '', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: 'members', label: 'Members', icon: UsersRound },
  { to: 'roles', label: 'Roles', icon: ShieldCheck },
  { to: 'files', label: 'Files', icon: FolderOpen, permission: 'documents.view' },
  { to: 'calendar', label: 'Calendar', icon: Calendar },
  { to: 'chat', label: 'Chat', icon: MessageSquare },
  { to: 'ai', label: 'AI', icon: Bot },
  { to: 'analytics', label: 'Analytics', icon: BarChart3 },
  { to: 'theme', label: 'Theme', icon: Palette, permission: 'projects.manage' },
  { to: 'settings', label: 'Settings', icon: Settings, permission: 'projects.manage' },
];

/** Tab shell for a single workspace — fetches the workspace once and hands it to nested pages via <Outlet context>. */
export function WorkspaceLayout() {
  const { id } = useParams<{ id: string }>();
  const workspaceId = Number(id);
  const navigate = useNavigate();

  const { data: workspace, isLoading } = useQuery({
    queryKey: ['workspaces', workspaceId],
    queryFn: () => workspacesApi.get(workspaceId),
  });

  if (isLoading || !workspace) {
    return <Spinner label="Loading workspace…" />;
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/workspaces')}
          aria-label="Back to workspaces"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        {workspace.logo_url ? (
          <img src={resolveFileUrl(workspace.logo_url) ?? undefined} alt={workspace.name} className="h-10 w-10 rounded-xl object-cover" />
        ) : (
          <div
            className="flex h-10 w-10 items-center justify-center rounded-xl text-lg"
            style={{ backgroundColor: (workspace.color ?? '#6366f1') + '20', color: workspace.color ?? '#6366f1' }}
          >
            {workspace.icon ?? workspace.name.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-xl font-semibold text-gray-900 dark:text-gray-100">{workspace.name}</h1>
          {workspace.description && <p className="truncate text-sm text-gray-500 dark:text-gray-400">{workspace.description}</p>}
        </div>
        <Badge tone={VISIBILITY_TONE[workspace.visibility]}>{workspace.visibility}</Badge>
      </div>

      <div className="-mb-px flex gap-1 overflow-x-auto border-b border-gray-200 dark:border-gray-800">
        {tabs.map((tab) => (
          <TabLink key={tab.to} tab={tab} />
        ))}
      </div>

      <Outlet context={workspace} />
    </div>
  );
}

function TabLink({ tab }: { tab: Tab }) {
  const allowed = usePermission(tab.permission ?? '__always__') || !tab.permission;
  if (!allowed) {
    return null;
  }

  return (
    <NavLink
      to={tab.to}
      end={tab.end}
      className={({ isActive }) =>
        `flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors ${
          isActive
            ? 'border-brand-600 text-brand-700 dark:border-brand-400 dark:text-brand-300'
            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
        }`
      }
    >
      <tab.icon className="h-4 w-4" />
      {tab.label}
    </NavLink>
  );
}

/** Nested workspace pages call this instead of re-fetching the workspace themselves. */
export function useWorkspaceContext(): Workspace {
  return useOutletContext<Workspace>();
}
